import { Box } from '@mui/material'
import { DataTable } from 'components/Shared/DataTable'
import { Colors, colorPrimitives } from 'components/Theme'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useGetAllFlaggedPhoneNumbers } from 'hooks/api/FlaggedPhoneNumbers/useGetAllFlaggedPhoneNumbers'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { useState } from 'react'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { type FlaggedPhoneNumber } from 'types/api'
import { EditFlaggedPhoneNumberModal } from './EditFlaggedPhoneNumberModal'
import { formatPhoneNumber } from 'utils/phoneUtils'

const getColumns = (): GridColDef[] => [
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return formatPhoneNumber(params.row.phoneNumber)
    },
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'email',
    headerName: 'Email',
    minWidth: 250,
    flex: 1,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'carrier',
    headerName: 'Carrier',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'enabled',
    headerName: 'Status',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      const status = params.row.enabled ? 'ENABLED' : 'DISABLED'
      return (
        <Box
          style={{
            backgroundColor: params.row.enabled
              ? Colors.licensedEstablishmentStatus.background.active
              : Colors.licensedEstablishmentStatus.background.inactive,
            color: colorPrimitives.black,
            fontWeight: 'bold',
            borderRadius: '4px',
            padding: '6px',
            display: 'inline-block',
          }}
        >
          {status}
        </Box>
      )
    },
  },
]

export const FlaggedPhoneNumbers = () => {
  const query = useGetAllFlaggedPhoneNumbers()
  const [filteredData, setFilteredData] = useState<FlaggedPhoneNumber[]>()
  const [phoneNumberToEdit, setPhoneNumberToEdit] =
    useState<FlaggedPhoneNumber>()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const handleFilteredData = (filteredData: FlaggedPhoneNumber[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.row.id) {
      setPhoneNumberToEdit({
        id: params.row.id,
        phoneNumber: params.row.phoneNumber ?? '',
        firstName: params.row.firstName ?? '',
        lastName: params.row.lastName ?? '',
        email: params.row.email ?? '',
        carrier: params.row.carrier ?? '',
        type: params.row.type ?? '',
        enabled: params.row.enabled,
      })
    }
    setIsEditModalOpen(true)
  }

  const toggleIsEditModalOpen = () => {
    setIsEditModalOpen(!isEditModalOpen)
  }

  return (
    <>
      <Box>
        <GridSearchField<'FlaggedPhoneNumberDTO'>
          placeholder="Search Flagged Phone Numbers"
          data={query.data ?? []}
          handleFilteredData={handleFilteredData}
          fullWidth
        />

        {query.isPending && <ActivityIndicator />}
        {query.isError && <p>An error occured.</p>}
        {!query.isError && query.data && (
          <DataTable
            columns={getColumns()}
            rows={filteredData ?? query.data}
            onCellClick={selectCell}
          />
        )}
      </Box>
      <EditFlaggedPhoneNumberModal
        flaggedPhoneNumberToEdit={phoneNumberToEdit}
        isModalOpen={isEditModalOpen}
        toggleModalIsOpen={toggleIsEditModalOpen}
      />
    </>
  )
}
